import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

const VimeoEmbed = ({ id }) => {
  return (
    <iframe
      className="responsive-video"
      id={`embed_${id}`}
      src={`//player.vimeo.com/video/${id}?badge=0&byline=0&portrait=0&title=0&dnt=true`}
      title="video embed from vimeo"
      width="480"
      height="270"
      frameBorder="0"
      webkitallowfullscreen=""
      mozallowfullscreen=""
      allowFullScreen
    />
  );
};

const VideoEmbed = ({ id, preview }) => {
  return (
    // eslint-disable-next-line
    <video id={`embed_${id}`} playsInline controls poster={preview ? preview : null}>
      <source src={`https://media.starlingbank.com/videos/${id}.webm`} type="video/webm" />
      <source src={`https://media.starlingbank.com/videos/${id}.mp4`} type="video/mp4" />
    </video>
  );
};

const YouTubeEmbed = ({ id, title }) => {
  const url = `https://www.youtube-nocookie.com/embed/${id}?showinfo=0&wmode=transparent&rel=0&color=white&modestbranding=1`;
  return (
    <iframe
      className="responsive-video"
      id={`embed_${id}`}
      src={url}
      title={title || 'video embed from youtube'}
      width="480"
      height="270"
      frameBorder="0"
      allowFullScreen
    />
  );
};

const YouTubeListEmbed = ({ id, preview, previewAlt }) => {
  const url = `https://www.youtube-nocookie.com/embed/videoseries?list=${id}&amp;rel=0`;
  return (
    <iframe
      className="responsive-video"
      id={`embed_${id}`}
      src={url}
      title="video playlist embed from youtube"
      width="480"
      height="270"
      frameBorder="0"
      allowFullScreen
    />
  );
};

const EmbedElement = ({ type, embedId, preview, embedTitle }) => {
  switch (type) {
    case 'vimeo':
      return <VimeoEmbed id={embedId} />;
    case 'video':
      return <VideoEmbed id={embedId} preview={preview} />;
    case 'youtube':
      return <YouTubeEmbed id={embedId} title={embedTitle} />;
    case 'youtubelist':
      return <YouTubeListEmbed id={embedId} />;
    default:
      return null;
  }
};

const Embed = (props) => {
  const { type, videoId, embedId, preview, title, legend, colourScheme, overHeader, customPadding, embedTitle } = props;

  const componentClasses = classNames('embed', {
    [`embed--${type}`]: type,
    [`embed--${colourScheme}`]: colourScheme,
    'embed--over-header': overHeader,
    'embed--no-padding-top': customPadding === 'no-top',
    'embed--no-padding-bottom': customPadding === 'no-bottom',
    'embed--no-padding-top-bottom': customPadding === 'no-padding'
  });

  const id = embedId || videoId;

  return (
    <figure className={componentClasses}>
      {title ? (
        legend ? (
          <h2>{title}</h2>
        ) : (
          <figcaption>
            <h2>{title}</h2>
          </figcaption>
        )
      ) : null}

      {type === 'youtube' || type === 'youtubelist' ? (
        <p className={`embed-disclaimer`}>
          By playing this video you agree to{' '}
          <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer">
            YouTube's
          </a>{' '}
          use of cookies. This use may include analytics, personalisation and ads.
        </p>
      ) : null}

      <div className={`embed-container`}>
        <EmbedElement type={type} embedId={id} preview={preview} embedTitle={embedTitle} />
      </div>

      {legend ? <figcaption>{legend}</figcaption> : null}
    </figure>
  );
};

Embed.propTypes = {
  preview: PropTypes.string,
  previewAlt: PropTypes.string,
  type: PropTypes.oneOf(['youtube', 'youtubelist', 'vimeo', 'video']).isRequired,
  videoId: idValidator,
  embedId: idValidator,
  embedRatio: PropTypes.oneOf(['16/9', '4/3']),
  legend: PropTypes.string,
  title: PropTypes.string,
  colourScheme: PropTypes.oneOf(['dark-navy', 'dark-purple', 'light-grey']),
  embedTitle: PropTypes.string
};

function idValidator(props, propName, componentName) {
  if (!props.videoId && !props.embedId) {
    return new Error('At least one of `videoId` or `embedId` must be provided to `Embed`');
  }
  return PropTypes.checkPropTypes({ [propName]: PropTypes.string }, props, propName, componentName);
}

export default Embed;
